import Shell from '../components/Shell';
import Seo from '../components/Seo';
import { PageHero, Section, CTA, DotCircle } from '../components/Pieces';
import TlmDiagramSlideshow from '../components/TlmDiagramSlideshow';
import { sevenEngines, implementationSteps, siteImages } from '../mock';
import { Cpu, BookOpen, Target, FileText, Factory, Users2, Radio, LineChart } from 'lucide-react';

const iconFor = (i) => [BookOpen, Target, FileText, Factory, Users2, Radio, LineChart][i] || Cpu;

export default function DigitalTLM() {
  return (
    <Shell>
      <Seo title="Digital TLM Engine"
        description="Seven connected engines turn curriculum into classroom-ready teaching learning material — with human review, teacher agency and evidence at every step."
        image={siteImages.digitalTlm} />
      <PageHero
        kicker="The Digital TLM Engine"
        title="Seven connected engines. One continuous learning cycle."
        sub="Learning intelligence, lessons, TLMs, teachers, classrooms and evidence — designed to support Classroom Execution rather than replace it."
        breadcrumbs={[{ label: 'Transform Education' }, { label: 'Digital TLM Engine' }]}
      />

      <Section bg="white">
        <div className="max-w-3xl">
          <div className="mm-kicker mb-3">The Framework, at a Glance</div>
          <h2 className="font-display text-2xl md:text-4xl font-bold text-[var(--mm-ink)] mm-rule">See the engine and its rollout.</h2>
          <p className="mt-6 text-[var(--mm-ink)]/70 leading-relaxed">Two views of the Digital TLM Engine — the seven connected engines and the 20-week implementation approach. Enlarge either diagram to read every stage and label.</p>
        </div>
        <div className="mt-10">
          <TlmDiagramSlideshow />
        </div>
      </Section>


      <Section bg="white">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div className="order-2 md:order-1">
            <div className="mm-kicker mb-3">At the Centre</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[var(--mm-ink)] mm-rule">Classroom Execution is not another module.</h2>
            <p className="mt-5 text-[var(--mm-ink)]/70 leading-relaxed">The seven engines exist to make what happens inside the classroom — teacher practice, student learning and engagement — measurably better. Everything else supports this outcome.</p>
          </div>
          <div className="order-1 md:order-2 flex items-center justify-center">
            <div className="relative w-72 h-72 md:w-96 md:h-96">
              <div className="absolute inset-0 mm-dot-halo rounded-full opacity-70" />
              <div className="absolute inset-6 rounded-full bg-[var(--mm-purple)] text-white flex flex-col items-center justify-center text-center px-6">
                <span className="font-display text-2xl md:text-3xl font-bold">Classroom<br/>Execution</span>
                <span className="mt-3 text-[11px] tracking-[0.18em] uppercase opacity-80">Teacher · Student · Engagement</span>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section bg="cream2">
        <div className="text-center max-w-2xl mx-auto">
          <div className="mm-kicker mb-3">The Seven Engines</div>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-[var(--mm-ink)] mm-rule-center">Curriculum → Classroom → Evidence.</h2>
        </div>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {sevenEngines.map((e, i) => {
            const Icon = iconFor(i);
            return (
              <div key={e.id} className="mm-card p-6">
                <div className="flex items-center gap-4 mb-5">
                  <DotCircle size={70}><Icon className="w-5 h-5"/></DotCircle>
                  <span className="mm-kicker">Engine {e.id}</span>
                </div>
                <h3 className="font-display text-xl font-bold text-[var(--mm-ink)]">{e.title}</h3>
                <p className="mt-2.5 text-sm text-[var(--mm-ink)]/70 leading-relaxed">{e.body}</p>
              </div>
            );
          })}
        </div>
      </Section>

      <Section bg="white">
        <div className="text-center max-w-2xl mx-auto">
          <div className="mm-kicker mb-3">Implementation Model</div>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-[var(--mm-ink)] mm-rule-center">From framework to classroom practice.</h2>
          <p className="mt-6 text-[var(--mm-ink)]/70">Build capability. Demonstrate classroom value. Decide on scale.</p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 gap-4">
          {implementationSteps.map((s) => (
            <div key={s.id} className="flex gap-5 bg-[var(--mm-cream)] border border-[var(--mm-line)] rounded-2xl p-6">
              <div className="font-display text-3xl font-bold text-[var(--mm-purple)]/50 shrink-0">{s.id}</div>
              <div>
                <h3 className="font-display text-lg font-bold text-[var(--mm-ink)]">{s.title}</h3>
                <p className="mt-1.5 text-sm text-[var(--mm-ink)]/70">{s.body}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <CTA title="Bring the Digital TLM Engine to your institution." />
    </Shell>
  );
}
